/**
 * Animations — port 1:1 din legacy common.js lines 1271-1583.
 *   - Node/NodeList.prototype.slideDown / slideUp / slideToggle
 *   - Node.prototype.dxFadeIn / dxFadeOut
 *
 * Foloseste Web Animations API (target.animate) ca-n legacy. slideBusy e
 * module-level (acelasi comportament cu IIFE original — un singur slide la
 * orice moment, force-ul il bypass-eaza).
 */

import { isfn } from './util.js';

interface SlideProps {
    display: string;
    style:   Record<string, string>;
    down:    Record<string, string>;
}

let slideBusy = false;

function setSlideProps(target: HTMLElement): SlideProps {
    const cs = window.getComputedStyle(target);

    let display      = cs.getPropertyValue('display');
    const overflow   = cs.getPropertyValue('overflow');
    const boxSizing  = cs.getPropertyValue('box-sizing');
    const paddingTop = parseInt(cs.getPropertyValue('padding-top').split('px')[0]  ?? '0');
    const paddingBot = parseInt(cs.getPropertyValue('padding-bottom').split('px')[0] ?? '0');
    const marginTop  = parseInt(cs.getPropertyValue('margin-top').split('px')[0]    ?? '0');
    const marginBot  = parseInt(cs.getPropertyValue('margin-bottom').split('px')[0] ?? '0');

    if (display === 'none') {
        if (target.style.display) {
            target.style.removeProperty('display');
            display = cs.getPropertyValue('display');
        }
        if (display === 'none') {
            target.style.display = 'block';
        }
    }

    let contentHeight = Math.max(target.offsetHeight - paddingTop - paddingBot, 0);
    if (boxSizing === 'border-box') {
        contentHeight = Math.max(target.offsetHeight, 0);
    }

    return {
        display,
        style: {
            height:        target.style.height,
            marginTop:     target.style.marginTop,
            marginBottom:  target.style.marginBottom,
            paddingTop:    target.style.paddingTop,
            paddingBottom: target.style.paddingBottom,
            overflow:      target.style.overflow,
            display:       target.style.display,
        },
        down: {
            overflow,
            height:        `${contentHeight}px`,
            marginTop:     `${marginTop}px`,
            marginBottom:  `${marginBot}px`,
            paddingTop:    `${paddingTop}px`,
            paddingBottom: `${paddingBot}px`,
        },
    };
}

function slideDownImpl(target: HTMLElement, callback?: (visible: boolean) => void, force?: boolean): void {
    if (slideBusy && !force) return;
    slideBusy = true;

    const props = setSlideProps(target);

    Object.assign(target.style, {
        height:        '0px',
        marginTop:     '0px',
        marginBottom:  '0px',
        paddingTop:    '0px',
        paddingBottom: '0px',
        overflow:      'hidden',
    });

    const anim = target.animate(props.down, { duration: 300, easing: 'linear', iterations: 1 });
    anim.onfinish = () => {
        Object.assign(target.style, props.style);
        callback?.call(target, true);
        slideBusy = false;
    };
}

function slideUpImpl(target: HTMLElement, callback?: (visible: boolean) => void, force?: boolean): void {
    if (slideBusy && !force) return;
    slideBusy = true;

    const props = setSlideProps(target);
    Object.assign(target.style, props.down);

    const anim = target.animate({
        height:        '0px',
        marginTop:     '0px',
        marginBottom:  '0px',
        paddingTop:    '0px',
        paddingBottom: '0px',
        overflow:      'hidden',
    }, { duration: 300, easing: 'linear', iterations: 1 });

    anim.onfinish = () => {
        Object.assign(target.style, props.style);
        target.style.display = 'none';
        callback?.call(target, false);
        slideBusy = false;
    };
}

Node.prototype.slideDown = function (this: Node, callback?: (v: boolean) => void, force?: boolean) {
    const el = this as HTMLElement;
    if (window.getComputedStyle(el).getPropertyValue('display') === 'none') {
        slideDownImpl(el, callback, force);
    }
    return this;
} as Node['slideDown'];

Node.prototype.slideUp = function (this: Node, callback?: (v: boolean) => void, force?: boolean) {
    const el = this as HTMLElement;
    if (window.getComputedStyle(el).getPropertyValue('display') !== 'none') {
        slideUpImpl(el, callback, force);
    }
    return this;
} as Node['slideUp'];

Node.prototype.slideToggle = function (this: Node, callback?: (v: boolean) => void, force?: boolean) {
    const el = this as HTMLElement;
    if (window.getComputedStyle(el).getPropertyValue('display') === 'none') {
        slideDownImpl(el, callback, force);
    } else {
        slideUpImpl(el, callback, force);
    }
    return this;
} as Node['slideToggle'];

NodeList.prototype.slideDown = function (this: NodeList, callback?: (v: boolean) => void, force?: boolean) {
    this.forEach((node) => { (node as Node).slideDown(callback, force); });
    return this;
} as NodeList['slideDown'];

NodeList.prototype.slideUp = function (this: NodeList, callback?: (v: boolean) => void, force?: boolean) {
    this.forEach((node) => { (node as Node).slideUp(callback, force); });
    return this;
} as NodeList['slideUp'];

NodeList.prototype.slideToggle = function (this: NodeList, callback?: (v: boolean) => void) {
    this.forEach((node) => { (node as Node).slideToggle(callback); });
    return this;
} as NodeList['slideToggle'];

/* ---------- dxFadeIn / dxFadeOut ---------- */

interface FadeOpts {
    duration?: number;
    display?:  string;
    complete?: (target: HTMLElement, visible: boolean) => void;
}

Node.prototype.dxFadeIn = function (this: Node, optsArg?: FadeOpts | ((target: HTMLElement, visible: boolean) => void)) {
    const target = this as HTMLElement;
    if (target.dataset['fadeBusy'] || target.dxGetProp('display') !== 'none') return;

    let opts: FadeOpts = { duration: 300, display: 'block' };
    if (isfn(optsArg)) {
        opts.complete = optsArg as FadeOpts['complete'];
    } else if (optsArg) {
        opts = { ...opts, ...optsArg };
    }

    target.dataset['fadeBusy'] = '1';
    target.classList.remove('hide');
    if (target.dxGetProp('display') === 'none') {
        target.style.display = opts.display!;
    }
    target.style.opacity = '0';

    const anim = target.animate({ opacity: 1 }, { duration: opts.duration!, easing: 'linear', iterations: 1 });
    anim.onfinish = () => {
        target.style.removeProperty('opacity');
        delete target.dataset['fadeBusy'];
        opts.complete?.call(target, target, true);
    };
};

Node.prototype.dxFadeOut = function (this: Node, optsArg?: FadeOpts | ((target: HTMLElement, visible: boolean) => void)) {
    const target = this as HTMLElement;
    if (target.dataset['fadeBusy'] || target.dxGetProp('display') === 'none') return;
    target.dataset['fadeBusy'] = '1';

    let opts: FadeOpts = { duration: 300 };
    if (isfn(optsArg)) {
        opts.complete = optsArg as FadeOpts['complete'];
    } else if (optsArg) {
        opts = { ...opts, ...optsArg };
    }

    target.style.opacity = '1';

    const anim = target.animate({ opacity: 0 }, { duration: opts.duration!, easing: 'linear', iterations: 1 });
    anim.onfinish = () => {
        target.classList.add('hide');
        target.style.removeProperty('opacity');
        delete target.dataset['fadeBusy'];
        opts.complete?.call(target, target, false);
    };
};
